// The FRONTEND face of the Agri Turnkey Solutions fork config seam.
//
// The screens import these named exports from '@client/config/client.config';
// in this fork the vite alias resolves HERE. Values MUST mirror the
// server-side seam (client-custom/config/client.config.js) — the config
// suite asserts the two never drift.

// the brand colours the product leaves to the site (CSS variables only) — see brand-css.js
import './brand-css.js';

export const BRAND_NAME = 'Agri Turnkey Solutions';
export const BRAND_TAGLINE = 'World Class Manufacturing and Engineering Systems';
export const BRAND_SUBTITLE = '';
export const APP_TITLE = 'Agri Turnkey Solutions — Zebra Stride';
export const CUSTOMER_LABEL = 'Customer';

// Must match a group name in the site's data (permission checks, admin UI).
export const ADMIN_GROUP = 'site-admins';

// The site-switchers group — membership reveals the site switcher (see
// client-custom/config/client.config.js). The frontend only decides whether to ASK for the
// list; the server decides whether to answer.
export const SITE_SWITCH_GROUP = 'site-switchers';

// Workspaces (see the server seam's workspacesEnabled): off unless a client
// turns it on. Off = no Settings entry, no /api/workspaces call, nothing drawn.
export const WORKSPACES_ENABLED = false;

// Browser storage namespace (localStorage, BroadcastChannel).
export const STORAGE_PREFIX = 'agri-turnkey-solutions';

// The architectural hierarchy (see client-custom/config/client.config.js).
export const PROJECT_ISSUE_TYPE = 'Job';
export const MODULE_ISSUE_TYPE = 'Module';
// Compared case-sensitively by several read paths (coupling: link-case).
export const LINK_TYPE_NAME = 'LINK';

// The field kanban groups/colours by — none wired yet.
export const WPO_FIELD = null;

// Accent colour as RGB (JS canvas + generated chrome) — the logo's blue leaf.
export const ACCENT_RGB = { r: 43, g: 109, b: 141 };

// Documents (PO, delivery note, invoice): the company's name and address
// under the logo, not beside it — on the paper and in its PDF. Read by the
// line-items document (ATS fork patch, RE-APPLY-ON-SYNC).
export const DOCUMENT_LETTERHEAD = 'stack';

// Client Customs proxy host allowlist (enforced server-side; mirrored here
// so the seam stays one shape on both faces).
export const CUSTOMS_ALLOWED_HOSTS = [];

// The Job's own screen: its GPS Location field IS the preliminary scope (the map, the site, the pipes'
// altitudes, Save on the job + its PDF) — the product's FIELD_VIEWS seam (work-items FieldView).
// Loaded through the '@client/customs' alias, never a relative path: web/vite.config.js reads this
// file (APP_TITLE) and Vite bundles its config — a relative import here drags the map into it and
// the build fails. On the Job screen it is headed "Zebra Preliminary Scoped Location" (his words,
// 2026-10-17) and folds away; the field itself keeps its name GPS Location everywhere else.
// A Quote's and a BOM's BOM Lines ARE the BOM on their own screens (2026-10-19): the BOM editor in place
// of the raw line-items table — standard BOMs and parts added right there, every line's stock and
// supply in words, and on a BOM the Pull from stock / Order what is short buttons
// (ats/bom/BomField.jsx). The field keeps its name.
export const FIELD_VIEWS = [
  {
    field: 'GPS Location',
    label: 'Zebra Preliminary Scoped Location',
    collapsible: true,
    load: () => import('@client/customs/ats/scope/ScopeField.jsx'),
  },
  {
    field: 'BOM Lines',
    collapsible: true,
    load: () => import('@client/customs/ats/bom/BomField.jsx'),
  },
  // The Job's Scope of Work IS its work items (round 13, 2026-10-21): drawn as the Jobs grid shows them,
  // a work item added from a dropdown of the work types (with their icons), one not started taken out
  // (ats/scope/WorkScopeField.jsx). The field keeps its name.
  {
    field: 'Scope of Work',
    collapsible: true,
    load: () => import('@client/customs/ats/scope/WorkScopeField.jsx'),
  },
  // Round 14 (2026-10-22, "WE CANNOT DEPEND ON CUSTOMS TO FACILITATE A PROCESS — IT NEEDS TO LIVE IN WORK
  // ITEMS"): a Stock Pull's lines are its PICK (the batch each line comes from, then Pulled — booked out),
  // a Procurement Order's Delivery Note is its RECEIVING (the supplier's delivery note photographed and
  // read, then booked in), an Installation's Delivery Note Lines get "Pull from the BOM" and Full screen
  // above the note. The fields keep their names.
  {
    field: 'Stock Pull Lines',
    load: () => import('@client/customs/ats/warehouse/StockPullField.jsx'),
  },
  {
    field: 'Delivery Note',
    load: () => import('@client/customs/ats/warehouse/OrderDeliveryField.jsx'),
  },
  {
    field: 'Delivery Note Lines',
    load: () => import('@client/customs/ats/install/DeliveryLinesField.jsx'),
  },
  // Time tracking (2026-11-02): a work item's Actual Time (Hours) is its time — logged against the
  // estimate, the timer (Start / Stop), Log time and the entries (ats/time/TimeField.jsx). The server
  // keeps the field's value: the sum of the entries.
  {
    field: 'Actual Time (Hours)',
    label: 'Time',
    load: () => import('@client/customs/ats/time/TimeField.jsx'),
  },
  // …its estimate is set in that widget: the Estimated Time (Hours) row hides while the widget is shown
  {
    field: 'Estimated Time (Hours)',
    load: () => import('@client/customs/ats/time/EstimateField.jsx'),
  },
];

// The Translator (a product feature, OFF until a site admin switches it on in Settings →
// Preferences): this site's own words — the ATS pages and the site's names — in Afrikaans,
// German and Polish. Loaded only when someone picks one of those languages.
export const TRANSLATIONS = {
  af: () => import('./translations/af.json'),
  de: () => import('./translations/de.json'),
  pl: () => import('./translations/pl.json'),
};
